import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component for hotkey-related components.
 * Catches and handles errors in CommandPalette, HelpDialog, and DebugOverlay.
 */
export class HotkeyErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("HotkeyErrorBoundary caught error:", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            padding: "16px",
            border: "1px solid #f5c6cb",
            borderRadius: "4px",
            backgroundColor: "#f8d7da",
            color: "#721c24",
            fontSize: "14px",
          }}
          role="alert"
        >
          <strong>Hotkey Component Error</strong>
          <p style={{ marginTop: "8px", marginBottom: 0 }}>
            Something went wrong with the hotkey system. {this.state.error?.message}
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}
